import { RealTimeResource } from '@nemuru-eng/js-api-client';
import { useRef } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { resolveSelectedPaymentMethodType } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { useGetCheckoutSessionById } from '@/hooks/requests/checkoutSession/useGetCheckoutSessionById';
import { useRealTimeResource } from '@/hooks/useRealTimeResource';

export const useLiveCheckoutSession = (checkoutSessionId: string | undefined) => {
  const { resetCache } = useGetCheckoutSessionById(checkoutSessionId);
  const { checkoutSessionEntity, selectedPaymentMethodType, update } = useCheckoutSessionContext();

  const refreshing = useRef(false);
  const pending = useRef(false);

  const selectedRef = useRef(selectedPaymentMethodType);
  selectedRef.current = selectedPaymentMethodType;

  const refresh = async () => {
    if (refreshing.current) {
      pending.current = true;
      return;
    }
    refreshing.current = true;
    try {
      if (checkoutSessionEntity) {
        const previousSelectedPaymentMethodType =
          checkoutSessionEntity.checkoutSession.selectedPaymentMethodType ?? undefined;
        await checkoutSessionEntity.refresh();
        await resetCache?.(checkoutSessionEntity.checkoutSession);
        const paymentMethodDetailEntities = await checkoutSessionEntity.createPaymentMethodDetailEntities();
        update({
          checkoutSessionEntity,
          paymentMethodDetailEntities,
          selectedPaymentMethodType: resolveSelectedPaymentMethodType(
            checkoutSessionEntity.checkoutSession,
            selectedRef.current,
            previousSelectedPaymentMethodType,
          ),
        });
      }
    } catch {
      // A failed refresh is not fatal: the next event (or the manual refresh button) retries.
    } finally {
      refreshing.current = false;
      if (pending.current) {
        pending.current = false;
        void refresh();
      }
    }
  };

  return useRealTimeResource({
    resource: RealTimeResource.CHECKOUT_SESSION,
    id: checkoutSessionId,
    enabled: Boolean(checkoutSessionEntity),
    onEvent: () => void refresh(),
  });
};
